import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444'
};

function UpdateDesignerTask() {
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [designers, setDesigners] = useState([]);

    // Form state - Only essential fields
    const [formData, setFormData] = useState({
        designer_id: '',
        video_name: '',
        status: 'pending',
        raw_link: '',
        description: ''
    });

    useEffect(() => {
        // Fetch designers list first
        fetchDesigners();
        
        // Load task data
        const taskData = sessionStorage.getItem('editTaskData');
        if (!taskData) {
            setError('No task data found. Please go back and select a task to edit.');
            setLoading(false);
            return;
        }

        try {
            const parsedTask = JSON.parse(taskData);
            setTask(parsedTask);
            
            // Initialize form data after designers are loaded
            setFormData({
                designer_id: parsedTask.designer_id || '',
                video_name: parsedTask.video_name || '',
                status: parsedTask.status || 'pending',
                raw_link: parsedTask.raw_link || '',
                description: parsedTask.description || ''
            });
            
        } catch (error) {
            setError('Invalid task data format.');
            setLoading(false);
        }
    }, []);

    const fetchDesigners = async () => {
        try {
            console.log('Fetching designers list...');
            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'get_designers_list'
                })
            });

            const text = await response.text();
            console.log('Designers API response:', text);
            
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                console.error('Error parsing designers list:', parseError);
                setError('Failed to load designers list');
                return;
            }
            
            if (data.success && data.designers) {
                console.log('Designers loaded:', data.designers);
                setDesigners(data.designers);
            } else {
                console.error('No designers found:', data.error);
                setError(data.error || 'No designers available');
            }
            
            setLoading(false);
            
        } catch (error) {
            console.error('Error fetching designers:', error);
            setError('Failed to load designers: ' + error.message);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        // Validate required fields - Only video name is required
        if (!formData.video_name.trim()) {
            setError('Video Name is required');
            setSaving(false);
            return;
        }

        try {
            const updateData = {
                operation: 'update_designer_task',
                task_id: task.id,
                designer_id: formData.designer_id || null,
                video_name: formData.video_name.trim(),
                description: formData.description.trim() || '', // Send empty string if not provided
                status: formData.status,
                raw_link: formData.raw_link.trim() || '' // Send empty string if not provided
            };

            console.log('Sending update data:', updateData);

            const response = await fetch(`${API_BASE}/designer_api.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateData)
            });

            const text = await response.text();
            console.log('Update API response:', text);
            
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to update task');
            }
            
            setSuccess('Task updated successfully!');
            setTimeout(() => {
                navigate('/all-designer-tasks');
            }, 2000);

        } catch (error) {
            console.error('Error updating task:', error);
            setError('Error updating task: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate('/all-designer-tasks');
    };

    // Get status options
    const statusOptions = [
        { value: 'pending', label: 'Pending', color: colors.warning },
        { value: 'todo', label: 'To Do', color: colors.secondary },
        { value: 'inprogress', label: 'In Progress', color: colors.accent },
        { value: 'completed', label: 'Completed', color: colors.success },
        { value: 'approved', label: 'Approved', color: colors.success },
        { value: 'needs_revision', label: 'Needs Revision', color: colors.danger }
    ];

    if (loading) {
        return (
            <Layout>
                <div style={{minHeight: '100vh', background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div style={{textAlign: 'center', color: 'white'}}>
                        <div className="loading-dots" style={styles.loadingDots}>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                            <div className="dot" style={styles.dot}></div>
                        </div>
                        <h3 style={styles.loadingText}>Loading Task Data</h3>
                        <p style={styles.loadingSubtext}>Preparing edit form...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{minHeight: '100vh'}}>
                <div className="update-task-container" style={styles.container}>
                    {/* Animated Background */}
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>

                    {/* Header Section */}
                    <div className="update-task-header" style={styles.header}>
                        <div className="header-content" style={styles.headerContent}>
                            <div className="welcome-section">
                                <button 
                                    onClick={handleBack}
                                    style={styles.backButton}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to All Tasks
                                </button>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-edit me-2"></i>
                                    Update Designer Task
                                </h1>
                                <p className="page-subtitle" style={styles.pageSubtitle}>
                                    Edit and update task details for: <strong style={{color: 'white'}}>{task?.video_name}</strong>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    {error && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(220, 53, 69, 0.1)',
                            border: `1px solid ${colors.danger}`,
                            color: colors.danger
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>Error</strong>
                                <span style={{color: 'white'}}>{error}</span>
                            </div>
                            <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    )}

                    {success && (
                        <div className="message-alert" style={{
                            ...styles.messageAlert,
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: `1px solid ${colors.success}`,
                            color: colors.success
                        }}>
                            <div className="alert-icon" style={{...styles.alertIcon, color: colors.success}}>
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div className="alert-message" style={styles.alertMessage}>
                                <strong>Success</strong>
                                <span style={{color: 'white'}}>{success}</span>
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="update-task-content" style={styles.content}>
                        <div className="form-card" style={styles.formCard}>
                            <div className="card-body" style={styles.cardBody}>
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        {/* Select Designer */}
                                        <div className="col-md-6 mb-4">
                                            <div className="form-section" style={styles.formSection}>
                                                <label style={styles.label}>
                                                    <i className="fas fa-user-circle me-2"></i>
                                                    Select Designer
                                                </label>
                                                <select
                                                    name="designer_id"
                                                    value={formData.designer_id}
                                                    onChange={handleInputChange}
                                                    style={styles.select}
                                                    className="custom-dropdown"
                                                >
                                                    <option value="">Choose a designer...</option>
                                                    {designers.map(designer => (
                                                        <option 
                                                            key={designer.id} 
                                                            value={designer.id}
                                                        >
                                                            {designer.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div style={styles.helpText}>
                                                    {designers.length === 0 ? 'No designers available' : `Choose from ${designers.length} available designers`}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Video Name */}
                                        <div className="col-md-6 mb-4">
                                            <div className="form-section" style={styles.formSection}>
                                                <label style={styles.label}>
                                                    <i className="fas fa-video me-2"></i>
                                                    View Task Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    name="video_name"
                                                    value={formData.video_name}
                                                    onChange={handleInputChange}
                                                    style={styles.input}
                                                    placeholder="Enter video project name"
                                                    required
                                                />
                                                <div style={styles.helpText}>
                                                    Enter the name of the video project
                                                </div>
                                            </div>
                                        </div>

                                        {/* Initial Status */}
                                        <div className="col-md-6 mb-4">
                                            <div className="form-section" style={styles.formSection}>
                                                <label style={styles.label}>
                                                    <i className="fas fa-tasks me-2"></i>
                                                    Initial Status
                                                </label>
                                                <select
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                    style={{
                                                        ...styles.select,
                                                        borderLeft: `4px solid ${statusOptions.find(opt => opt.value === formData.status)?.color || colors.warning}`
                                                    }}
                                                    className="custom-dropdown"
                                                >
                                                    {statusOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div style={styles.helpText}>
                                                    Current status of the task
                                                </div>
                                            </div>
                                        </div>

                                        {/* Raw Video Link */}
                                        <div className="col-12 mb-4">
                                            <div className="form-section" style={styles.formSection}>
                                                <label style={styles.label}>
                                                    <i className="fas fa-link me-2"></i>
                                                    Raw Video Link <span style={styles.optional}>(Optional)</span>
                                                </label>
                                                <input
                                                    type="url"
                                                    name="raw_link"
                                                    value={formData.raw_link}
                                                    onChange={handleInputChange}
                                                    style={styles.input}
                                                    placeholder="https://drive.google.com/raw-video-link (optional)"
                                                />
                                                <div style={styles.helpText}>
                                                    Optional: Provide the direct link to raw video files (Google Drive, Dropbox, etc.)
                                                </div>
                                            </div>
                                        </div>

                                        {/* Task Description & Requirements */}
                                        <div className="col-12 mb-4">
                                            <div className="form-section" style={styles.formSection}>
                                                <label style={styles.label}>
                                                    <i className="fas fa-file-alt me-2"></i>
                                                    Task Description & Requirements <span style={styles.optional}>(Optional)</span>
                                                </label>
                                                <textarea
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    style={styles.textarea}
                                                    rows="5"
                                                    placeholder="Describe the task requirements, specifications, and any important details for the designer... (optional)"
                                                />
                                                <div style={styles.helpText}>
                                                    Optional: Detailed description of what needs to be done
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="form-actions" style={styles.formActions}>
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            style={styles.cancelButton}
                                            disabled={saving}
                                        >
                                            <i className="fas fa-times me-2"></i>
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            style={styles.submitButton}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                                    Updating Task...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                    Update Task
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <style jsx>{`
                    @keyframes float {
                        0%, 100% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                    }

                    @keyframes bounce {
                        0%, 80%, 100% {
                            transform: scale(0);
                        } 40% {
                            transform: scale(1);
                        }
                    }

                    .form-section {
                        transition: all 0.3s ease;
                    }

                    .form-section:focus-within {
                        transform: translateY(-2px);
                    }

                    input:focus, select:focus, textarea:focus {
                        outline: none;
                        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
                    }
                `}</style>

                {/* Global styles for dropdown */}
                <style jsx global>{`
                    .custom-dropdown {
                        appearance: none;
                        -webkit-appearance: none;
                        -moz-appearance: none;
                        background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
                        background-repeat: no-repeat;
                        background-position: right 15px center;
                        background-size: 12px;
                    }

                    .custom-dropdown option {
                        background-color: #302b63;
                        color: white;
                        padding: 12px;
                    }

                    .custom-dropdown:focus {
                        background-image: url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%236366F1' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>");
                    }

                    /* For Webkit browsers (Chrome, Safari) */
                    .custom-dropdown::-webkit-scrollbar {
                        width: 8px;
                    }

                    .custom-dropdown::-webkit-scrollbar-track {
                        background: rgba(255, 255, 255, 0.1);
                        border-radius: 4px;
                    }

                    .custom-dropdown::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.3);
                        border-radius: 4px;
                    }

                    .custom-dropdown::-webkit-scrollbar-thumb:hover {
                        background: rgba(255, 255, 255, 0.5);
                    }

                    /* For Firefox */
                    .custom-dropdown {
                        scrollbar-width: thin;
                        scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
                    }
                `}</style>
            </div>
        </Layout>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 20px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        maxWidth: '1000px',
        margin: '0 auto'
    },
    backButton: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        marginBottom: '20px',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        fontWeight: '600'
    },
    pageTitle: {
        fontSize: '2.5rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: '0 0 10px 0'
    },
    pageSubtitle: {
        color: colors.textLight,
        fontSize: '1.1rem',
        margin: 0
    },
    messageAlert: {
        color: 'white',
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        maxWidth: '1000px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alertIcon: {
        fontSize: '1.5rem'
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1000px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    formCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid ${colors.borderDark}`,
        backdropFilter: 'blur(10px)'
    },
    cardBody: {
        padding: '40px'
    },
    formSection: {
        marginBottom: '0'
    },
    label: {
        color: 'white',
        fontWeight: '600',
        marginBottom: '12px',
        display: 'block',
        fontSize: '1rem'
    },
    optional: {
        color: colors.accent,
        fontSize: '0.8rem',
        fontStyle: 'italic',
        fontWeight: 'normal',
        marginLeft: '5px'
    },
    input: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `1px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        marginBottom: '8px'
    },
    select: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `1px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        marginBottom: '8px'
    },
    textarea: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `1px solid ${colors.borderDark}`,
        borderRadius: '12px',
        padding: '15px 20px',
        color: 'white',
        fontSize: '1rem',
        resize: 'vertical',
        minHeight: '120px',
        transition: 'all 0.3s ease',
        marginBottom: '8px'
    },
    helpText: {
        color: colors.textLight,
        fontSize: '0.85rem',
        marginTop: '5px',
        fontStyle: 'italic'
    },
    formActions: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'flex-end',
        marginTop: '40px',
        paddingTop: '30px',
        borderTop: `1px solid ${colors.borderDark}`
    },
    cancelButton: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid ${colors.borderDark}`,
        padding: '15px 30px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        fontSize: '1rem'
    },
    submitButton: {
        background: colors.primary,
        color: 'white',
        border: 'none',
        padding: '15px 40px',
        borderRadius: '12px',
        cursor: 'pointer',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        fontSize: '1.1rem'
    },
    loadingDots: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '20px'
    },
    dot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: colors.primary,
        animation: 'bounce 1.4s ease-in-out infinite both'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600',
        color: 'white'
    },
    loadingSubtext: {
        color: colors.textLight,
        margin: 0,
        fontSize: '1.1rem'
    }
};

export default UpdateDesignerTask;